import { LocalizationProvider, MobileDateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { Button, Divider, Grid, Paper, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import VendorLibraries from "components/vendorLibraries";
import VendorSelect from "components/vendorSelect";
import { isValid } from "date-fns";
import { useSnackbar } from "notistack";
import React, { useCallback, useState } from "react";
import { getVendorDashboardData } from "services/vendorDashboard";
import { getDbFormat } from "utils/common";
import InfoCard from "../InfoCard";
import { AccountBalanceWalletRounded, CardMembershipRounded, LocalOfferRounded, MessageRounded } from "@mui/icons-material";
import FullScreenModal from "components/FullScreenModal";
import VendorCoupons from "./Coupons";
import VendorEnquiries from "./Enquiries";
import VendorPasses from "./Passes";
import VendorExpenses from "./Expenses";
import WeSecureWallet from "./WeSecure";
import VendorIncentives from "./Incentives";
import VendorDues from "./Dues";
import VendorPayouts from "./Payouts";
import VendorBookings from "./Bookings";
import Modal from "components/Modal";
import VendorRevenue from "./Revenue";
import VendorSeatLayout from "./SeatLayout";

const themeNames = [
  "yellow","red","orange","purple","green","blue",
];

const dashboardPrimaryCards = [
  {title: 'We Secure Balance', key: 'weSecureBalance', theme: 'yellow', icon: <AccountBalanceWalletRounded sx={{fontSize: 70}} />},
  {title: 'Coupons', key: 'coupons', theme: 'blue', icon: <LocalOfferRounded sx={{fontSize: 70}} />},
  {title: 'Passes', key: 'passes', theme: 'green', icon: <CardMembershipRounded sx={{fontSize: 70}} />},
  {title: 'Enquiries', key: 'enquiries', icon: <MessageRounded sx={{fontSize: 70}} /> },
];

const dashboardSecondaryCards = [
  {title: 'Revenue', key: 'revenue', theme: 'cyan'},
  // {title: 'Walk-In Revenue', key: 'walkInBookingRevenue', theme: 'deepOrange'},
  // {title: 'Online Revenue', key: 'onlineBookingRevenue', theme: 'teal'},
  {title: 'Bookings', key: 'bookings', theme: 'green' },
  {title: 'Incentives', key: 'incentives', theme: 'amber' },
  {title: 'Payouts', key: 'payouts', theme: 'grey' },
  {title: 'Dues', key: 'dues', theme: 'brown' },
  {title: 'Expenses', key: 'expenses', theme: 'blueGrey'},
  {title: 'Seat Availability', key: 'vacantSeats', theme: 'blueGrey'},
];

export default function VendorDashboardPage() {

  const [ filterDates, setFilterDates] = useState([new Date(), new Date()]);
  const [ pageFilters, setPageFilters] = useState({filterDates});
  const [dashboarData, setDashboarData] = useState(null);
  const [cardType, setCardType] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openFlexiModal, setOpenFlexiModal] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const getVedorDashboardDetails = useCallback((pageFilters) => {
    let [from, to ]= pageFilters.filterDates;
    from = getDbFormat(from);
    to = getDbFormat(to);
    const propertyId= pageFilters.library.id;
    const vendorId= pageFilters.vendor.userId;
    // setDashboarData(resp);

    getVendorDashboardData({from, to, propertyId, vendorId, propertyType: 'LIBRARY'}).then(({response, error}) => {
      if(response){
        setDashboarData({...response});
      } else {
        setDashboarData(null);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }, [enqueueSnackbar]);

  
  const onVendorSelected = useCallback((vendor) => {
    if(pageFilters?.vendor?.userId !== vendor?.userId){
      setPageFilters({...pageFilters, vendor});
    }
  }, [pageFilters]);


  const onGoBtnClick = useCallback(() => {
    if(!(pageFilters?.filterDates)){
      enqueueSnackbar("Select from date and to date", {variant: 'error'});
      return;
    }
    if(!(pageFilters?.vendor)){
      enqueueSnackbar("Select vendor", {variant: 'error'});
      return;
    }
    if(!(pageFilters?.library)){
      enqueueSnackbar("Select library", {variant: 'error'});
      return;
    }
    getVedorDashboardDetails(pageFilters);

  }, [enqueueSnackbar, pageFilters, getVedorDashboardDetails]);

  function onDateAccepted(data) {
    const [fromDate,  toDate] = data;
    if(isValid(fromDate) && isValid(toDate)) {
      setFilterDates(data);
      setPageFilters({...pageFilters, filterDates: data});
    }else{
      enqueueSnackbar(`Select ${fromDate === null ? 'From Date' : 'To Date'}`, {variant: 'error'});
    }
  }

  const onLibrarySelected = useCallback((library) => {
    if(pageFilters?.library?.id !== library?.id){
      setPageFilters({...pageFilters, library});
    }
  }, [pageFilters]);

  const onCardClick = useCallback((type) => {
    const allowedTypes = ['coupons', 'passes', 'enquiries', 'enquiries', 'expenses', 'weSecureBalance', 'incentives', 'dues', 'payouts', 'bookings', 'vacantSeats'];
    const flexiModalAllowedTypes = ['revenue'];
    
    if(flexiModalAllowedTypes.includes(type)){
      setCardType(type);
      setOpenFlexiModal(true);
      return;
    }else{
      setOpenFlexiModal(false);
      setCardType(null);
    }

    if(allowedTypes.includes(type)){
      setCardType(type);
      setOpenModal(true);
    }else{
      setOpenModal(false);
      setCardType(null);
    }
  }, []);
  
  function getModalTitle(type) {
    const [found] = [...dashboardPrimaryCards, ...dashboardSecondaryCards].filter(({key}) => type === key);
    const { title=''} = found || {};
    return title;
  }

  const onModalClose = useCallback(() => {
      setOpenModal(false);
  }, [setOpenModal]);


  return (
    <Paper elevation={0} sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
      <Typography component="h2" variant="h6" color="secondary" gutterBottom sx={{mb: 1}}>
        Vendor Dashboard
      </Typography>
      <Divider />
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container >
          <Grid container item xs={12} sm={12} md={12} xl={12} lg={12} sx={{p:2, my: 1, bgcolor: "#f3f3f3"}}>
            <Typography variant="subtitle2" gutterBottom component="div">
              Filters
            </Typography>
            <Grid container item xs={12} sm={12} md={12} xl={12} lg={12}  columnSpacing={1} >
              <Grid item sx={{mt: 2}} xs={12} sm={12} md={4} xl={4} lg={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <MobileDateRangePicker
                    inputFormat="dd/MM/yyyy"
                    startText="From Date"
                    endText="To Date"
                    value={filterDates}
                    // clearable={true}
                    onChange={(newValue) => {
                      // setFilterDates(newValue);
                    }}
                    onAccept={(data) => onDateAccepted(data)}
                    renderInput={(startProps, endProps) => (
                      <React.Fragment>
                        <TextField {...startProps} size="small" onKeyDown={(e) => {e.preventDefault();return false}}/>
                        <Box sx={{ mx: 2 }}> to </Box>
                        <TextField {...endProps} size="small" onKeyDown={(e) => {e.preventDefault();return false}} />
                      </React.Fragment>
                    )}
                    />
                </LocalizationProvider>
              </Grid>
              <Grid item sx={{pt: 2}} xs={12} sm={5} md={3} xl={3} lg={3}>
                <VendorSelect onVendorSelected={onVendorSelected}/>
              </Grid>
              <Grid item sx={{pt: 2}} xs={12} sm={5} md={3} xl={3} lg={3}>
                <VendorLibraries onLibrarySelected={onLibrarySelected} variant="outlined" vendorId={pageFilters?.vendor?.userId} />
              </Grid>
              <Grid item sx={{pt: 2}} xs={4} sm={2} md={2} xl={2} lg={2}>
                <Button variant="contained" onClick={onGoBtnClick} sx={{textTransform:'none', fontSize: 14, py: '7px'}}>Go</Button>
              </Grid>
            </Grid>
          </Grid>

          <Grid container item xs={12} sm={12} md={12} lg={12} xl={12} spacing={3} my={1}>
            {dashboarData && dashboardPrimaryCards.map((info, i) => (
              <Grid item xs={12} sm={6} md={4} xl={3} lg={3} key={info.key}>
                <InfoCard title={info.title} value={dashboarData[info.key]} theme={info?.theme || themeNames[i%4]} icon={info.icon} type={info.key} onclick={onCardClick}/>
              </Grid>
            ))}
          </Grid>
          <Grid container item xs={12} sm={12} md={12} lg={12} xl={12} spacing={3} mt={3}>
            {dashboarData && dashboardSecondaryCards.map((info, i) => (
              <Grid item xs={12} sm={6} md={4} xl={3} lg={3} key={info.key}>
                <InfoCard title={info.title} value={dashboarData[info.key]} theme={info?.theme || themeNames[i%4]} type={info.key} onclick={onCardClick} />
              </Grid>
            ))}
          </Grid>
          <FullScreenModal open={openModal} onModalClose={onModalClose} title={getModalTitle(cardType)}>
            {cardType === 'coupons' ? <VendorCoupons vendorId={pageFilters?.vendor?.userId}/>: null}
            {cardType === 'enquiries' ? <VendorEnquiries vendorId={pageFilters?.vendor?.userId}/>: null}
            {cardType === 'passes' ? <VendorPasses vendorId={pageFilters?.vendor?.userId}/>: null}
            {cardType === 'expenses' ? <VendorExpenses pageFilters={pageFilters}/>: null}
            {cardType === 'incentives' ? <VendorIncentives pageFilters={pageFilters}/>: null}
            {cardType === 'dues' ? <VendorDues pageFilters={pageFilters}/>: null}
            {cardType === 'payouts' ? <VendorPayouts pageFilters={pageFilters}/>: null}
            {cardType === 'bookings' ? <VendorBookings pageFilters={pageFilters}/>: null}
            {cardType === 'vacantSeats' ? <VendorSeatLayout pageFilters={pageFilters}/>: null}
            {cardType === 'weSecureBalance' ? <WeSecureWallet vendorId={pageFilters?.vendor?.userId}/>: null}
          </FullScreenModal>
          <Modal  maxWidth={"sm"} open={openFlexiModal} onModalClose={() => setOpenFlexiModal(false)} title={getModalTitle(cardType)}>
            {cardType === 'revenue' ? <VendorRevenue  data={dashboarData} />: null}
          </Modal>
        </Grid>
      </Box>
    </Paper>
  );
}
